import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const formulaElements = [
  { label: "STRUCTURE", icon: "◇" },
  { label: "SYSTEM", icon: "◈" },
  { label: "FORMULA", icon: "◆" },
];

const SceneFormula = () => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  const lineWidth = useTransform(scrollYProgress, [0.1, 0.4], ["0%", "100%"]);

  return (
    <section 
      ref={sectionRef}
      className="scene relative min-h-screen flex items-center justify-center overflow-hidden"
      id="formula"
    >
      {/* Clean gradient - order restored */}
      <div className="absolute inset-0 bg-gradient-to-b from-frmla-dark via-background to-background" />
      
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="hsl(var(--foreground))" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      <motion.div 
        className="relative z-10 flex flex-col items-center text-center px-6 max-w-5xl"
        style={{ opacity }}
      >
        {/* The reveal */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <p className="font-body text-sm tracking-[0.3em] text-accent uppercase mb-8">
            THERE IS A BETTER WAY
          </p>
          <h2 className="font-display text-hero text-foreground">
            DESIGN BY
          </h2>
          <h2 className="font-display text-massive text-accent">
            FORMULA
          </h2>
        </motion.div>
        
        {/* Gold line reveal */}
        <motion.div 
          className="w-full max-w-lg h-[2px] bg-accent mb-16"
          style={{ width: lineWidth }}
        />
        
        {/* Formula elements */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 w-full max-w-4xl">
          {formulaElements.map((element, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.2,
                ease: [0.16, 1, 0.3, 1]
              }}
              viewport={{ once: true }}
              className="flex flex-col items-center"
            >
              <span className="text-5xl text-accent mb-4">{element.icon}</span>
              <span className="font-display text-2xl text-foreground tracking-wider">
                {element.label}
              </span>
            </motion.div>
          ))}
        </div>
        
        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="font-body text-base md:text-lg text-muted-foreground mt-16 max-w-2xl"
        >
          Repeatable systems that scale. Consistent results every time.
          <br />
          <span className="text-foreground">Your brand deserves more than guesswork.</span>
        </motion.p>
      </motion.div>
    </section>
  );
};

export default SceneFormula;
